import { type Row } from '@tanstack/react-table';
import { type FilterState } from '../components/action-bar/filter-menu';
import { MIN_ROW_HEIGHT } from '../data-grid.constants';
import type {
	DataGridActionDisabledReason,
	DataGridBaseRow,
	DataGridRowsHeight,
	DataGridSelectedRowsMetadata,
	IRowSelectionDisabledResolved,
} from '../data-grid.type';
import { IItemsCounts } from '../hooks/use-api';
import type { DataGridRow } from '../data-grid.type';

/**
 * Normalizes `disableRowSelection` to a boolean plus optional tooltip message for UI.
 * `{ message: string }` from the function form is treated as disabled for selection state.
 */
export function resolveRowSelectionDisabled<T extends DataGridBaseRow>(
	disableRowSelection: boolean | ((row: T) => boolean | DataGridActionDisabledReason) | undefined,
	row: T
): IRowSelectionDisabledResolved {
	if (disableRowSelection === undefined) {
		return { isDisabled: false };
	}

	if (typeof disableRowSelection === 'boolean') {
		return { isDisabled: disableRowSelection };
	}

	const result = disableRowSelection(row);

	if (typeof result === 'boolean') {
		return { isDisabled: result };
	}

	return { isDisabled: true, tooltipMessage: result.message };
}

/**
 * Extracts all selected rows from the selection state
 *
 * @param selectedRows - Map of page indices to row selection states
 * @param isAllPagesSelected - Whether all rows across all pages are selected
 * @returns Array of selected row data objects
 */
export function getSelectedRows<T>(
	selectedRows: Record<string, Record<string, boolean>>,
	rows: Row<T>[],
	currentSelectedRows: T[],
	isAllPagesSelected: boolean,
	uniqueField: keyof T,
	filters: Record<string, FilterState>,
	totalRowsCount: number,
	search?: string
): DataGridSelectedRowsMetadata<T> {
	// Collect all selected row IDs from all pages
	const selectedRowIds = new Set<string | number>();
	const excludedRowIds = new Set<string | number>();

	Object.values(selectedRows).forEach(pageSelections => {
		Object.entries(pageSelections).forEach(([id, isSelected]) => {
			if (isSelected) {
				selectedRowIds.add(id);
			} else {
				excludedRowIds.add(id);
			}
		});
	});

	// contains all selected rows from all pages and current page rows
	const rowsWithCurrentSelected = [...currentSelectedRows, ...rows.map(row => row.original)];

	const selectedRowsMatched = isAllPagesSelected
		? rowsWithCurrentSelected.filter(row => {
				const isExcluded = excludedRowIds.has(String(row[uniqueField]));
				const isDisabled = !!(row as DataGridRow<T>).__isDisabled;
				return !isExcluded && !isDisabled;
			})
		: [...selectedRowIds].flatMap(rowId => {
				// Find the corresponding row in the TanStack rows
				const tableRow = rowsWithCurrentSelected.find(row => String(row[uniqueField]) === String(rowId));
				// If found, return the original data
				return tableRow ? [tableRow] : [];
			});

	// De-duplicate rows by uniqueField
	const uniqueSelectedRowsMap = new Map<string, T>();
	selectedRowsMatched.forEach(row => {
		uniqueSelectedRowsMap.set(String(row[uniqueField]), row);
	});
	const uniqueSelectedRows = [...uniqueSelectedRowsMap.values()];

	return {
		all: isAllPagesSelected,
		selectedRows: uniqueSelectedRows,
		excludedIds: isAllPagesSelected ? [...excludedRowIds] : [],
		filters,
		search,
		totalRowsCount,
		selectedRowsCount:
			isAllPagesSelected && totalRowsCount ? totalRowsCount - excludedRowIds.size : uniqueSelectedRows.length,
	};
}

/**
 * Checks if two values are deeply equal
 * Optimized for DataGrid row comparisons
 */
export function isEqual(a: any, b: any): boolean {
	// Handle simple cases first
	if (a === b) return true;
	if (a == null || b == null) return a === b;

	// Handle different types
	const typeA = typeof a;
	const typeB = typeof b;
	if (typeA !== typeB) return false;

	// Handle array case (most common for DataGrid row selections)
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false;

		// Special optimization for rows with IDs (common in DataGrid)
		// First, check if these are arrays of objects with IDs
		if (
			a.length > 0 &&
			b.length > 0 &&
			typeof a[0] === 'object' &&
			typeof b[0] === 'object' &&
			a[0] !== null &&
			b[0] !== null &&
			'id' in a[0] &&
			'id' in b[0]
		) {
			// Create ID maps for fast lookup
			const aMap = new Map(a.map(item => [item.id, item]));
			const bMap = new Map(b.map(item => [item.id, item]));

			// Check that all IDs match
			if (aMap.size !== bMap.size) return false;

			// Check that all items with same ID are equal
			for (const [id] of aMap) {
				const bItem = bMap.get(id);

				if (!bItem) return false;
			}

			return true;
		}

		// Fallback for arrays of primitives or without IDs
		for (let i = 0; i < a.length; i++) {
			if (!isEqual(a[i], b[i])) return false;
		}

		return true;
	}

	// Handle object case
	if (typeA === 'object') {
		const keysA = Object.keys(a);
		const keysB = Object.keys(b);

		if (keysA.length !== keysB.length) return false;

		for (const key of keysA) {
			if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
			if (!isEqual(a[key], b[key])) return false;
		}

		return true;
	}

	// For other types (functions, symbols), just use strict equality
	return a === b;
}

export default isEqual;

/**
 * Returns whether a row is selected.
 * @param currentPageSelections - The selected rows record for the current page
 * @param isAllPagesSelected - Whether all pages are selected
 * @param rowId - The ID of the row to check
 * @returns
 */
export function isRowSelected({
	currentPageSelections,
	isAllPagesSelected,
	rowId,
	isDisabled,
}: {
	currentPageSelections: Record<string, boolean>;
	isAllPagesSelected: boolean;
	rowId: string | number;
	isDisabled: boolean;
}): boolean {
	const isSelected = isAllPagesSelected
		? currentPageSelections[rowId] !== false && !isDisabled // Selected unless explicitly excluded or
		: Boolean(currentPageSelections[rowId]); // Selected only if explicitly included

	return isSelected;
}

export const getRowsMinHeight = (rowsHeight: DataGridRowsHeight | number): number => {
	return typeof rowsHeight === 'number' ? rowsHeight : MIN_ROW_HEIGHT[rowsHeight];
};

export const getActionableItemCount = (meta: IItemsCounts): number => meta.actionableTotalItems ?? meta.totalItems ?? 0;
